/**
 * One door for a visit or an appointment: the house segmented toggle at
 * the top, Visit | Appointment, switching faces without losing what was
 * typed on either. Opened by the Appointments card's one door, "+ Visit
 * or appointment" (LP-42), on the Appointment face, and by the My Clients
 * page's visit door, on the Visit face, which names the client. The face
 * it opens on is the caller's: the dialog has no default of its own.
 *
 * Visit: a care manager's own planned visit, a date, an optional time and
 * one line (POST /clients/{clientId}/care-visits). The office
 * (manage_client_identity) picks the care manager; anyone else's visit is
 * their own, and the server holds that rule.
 *
 * Appointment: the record's Add appointment (RowEditDialog, create mode),
 * trimmed: provider and date, with time and purpose as two small boxes;
 * status, repeats, the Where block and notes behind one quiet "More".
 * Stored fields and the wire are as before; it opens scheduled, not
 * repeating (the registry's defaults).
 *
 * A date typed on one face carries to the other while that box is empty.
 *
 * The title names the client on both faces ("Add a visit for Bo", "Add an
 * appointment for Bo"), by the name the record calls them
 * (clientCallName: the preferred name, else the first name), so a dialog
 * opened from My Clients says whose visit it is and the toggle keeps the
 * title's shape.
 */
import { useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog } from '@/components/primitives/Dialog';
import { Button } from '@/components/primitives/Button';
import { Field } from '@/components/primitives/Field';
import { DateInput } from '@/components/primitives/DateInput';
import { SelectField } from '@/components/primitives/SelectField';
import { TextInput } from '@/components/primitives/TextInput';
import { SegmentedToggle } from '@/components/primitives/SegmentedToggle';
import { FLD } from '@/components/primitives/modalFormStyles';
import { RowEditDialog } from '@/components/clientRecord/RowEditDialog';
import { useTenant } from '@/lib/auth';
import { canManageClientIdentity } from '@/lib/capabilities';
import { careVisitsApi, refreshAfterCareVisitChange, type CareVisitInput } from '@/lib/careVisitsApi';
import { useTeamDirectory } from '@/lib/teamDirectory';
import { pageWriteText, serverSentence } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';

export type VisitOrAppointmentFace = 'visit' | 'appointment';

/** The Appointment face's own boxes; the rest wait behind More. */
const APPOINTMENT_FACE = ['provider', 'scheduled_for', 'scheduled_time', 'purpose'] as const;
const APPOINTMENT_MORE = ['status', 'occurred_on', 'recurrence', 'location_name', 'location_address', 'outcome'] as const;

const VISIT_NOTE_MAX = 200;

/** The dialog's title on each face, naming the client when there is a name. */
export function visitDialogTitle(face: VisitOrAppointmentFace, clientName: string | null): string {
  const what = face === 'visit' ? 'Add a visit' : 'Add an appointment';
  return clientName === null ? what : `${what} for ${clientName}`;
}

interface VisitDraft {
  rosterId: string;
  date: string;
  time: string;
  note: string;
}

export function VisitOrAppointmentDialog({
  clientId,
  clientName,
  face: openOn,
  onClose,
}: {
  clientId: string;
  /** The name the record calls the client by (clientCallName); null when
   *  there is none. */
  clientName: string | null;
  face: VisitOrAppointmentFace;
  onClose: () => void;
}): ReactNode {
  const [face, setFace] = useState<VisitOrAppointmentFace>(openOn);
  const [visit, setVisit] = useState<VisitDraft>({ rosterId: '', date: '', time: '', note: '' });
  const [appointment, setAppointment] = useState<Record<string, string>>({});

  const switchTo = (next: VisitOrAppointmentFace): void => {
    if (next === face) return;
    if (next === 'appointment') {
      setAppointment((a) => ({
        ...a,
        ...(visit.date !== '' && (a['scheduled_for'] ?? '') === '' ? { scheduled_for: visit.date } : {}),
        ...(visit.time !== '' && (a['scheduled_time'] ?? '') === '' ? { scheduled_time: visit.time } : {}),
      }));
    } else {
      setVisit((v) => ({
        ...v,
        date: v.date === '' ? (appointment['scheduled_for'] ?? '') : v.date,
        time: v.time === '' ? (appointment['scheduled_time'] ?? '').slice(0, 5) : v.time,
      }));
    }
    setFace(next);
  };

  const toggle = (
    <div className="mb-3" data-testid="visit-face-toggle">
      <SegmentedToggle<VisitOrAppointmentFace>
        options={[
          { value: 'visit', label: 'Visit', suffix: 'visit' },
          { value: 'appointment', label: 'Appointment', suffix: 'appointment' },
        ]}
        value={face}
        onChange={switchTo}
        name="Visit or appointment"
        testIdPrefix="visit-face"
      />
    </div>
  );

  if (face === 'appointment') {
    return (
      <RowEditDialog
        mode="create"
        projection="appointments"
        rowId=""
        clientId={clientId}
        title={visitDialogTitle('appointment', clientName)}
        values={appointment}
        header={toggle}
        fields={APPOINTMENT_FACE}
        more={APPOINTMENT_MORE}
        phonePairs={['scheduled_time', 'purpose']}
        onValuesChange={setAppointment}
        onClose={onClose}
      />
    );
  }
  return (
    <VisitFace
      clientId={clientId}
      title={visitDialogTitle('visit', clientName)}
      draft={visit}
      onDraft={setVisit}
      header={toggle}
      onClose={onClose}
    />
  );
}

function VisitFace({
  clientId,
  title,
  draft,
  onDraft,
  header,
  onClose,
}: {
  clientId: string;
  title: string;
  draft: VisitDraft;
  onDraft: (next: VisitDraft) => void;
  header: ReactNode;
  onClose: () => void;
}): ReactNode {
  const { tenantSlug = '' } = useParams<{ tenantSlug: string }>();
  const { whoami } = useTenant();
  const office = canManageClientIdentity(whoami);
  const directory = useTeamDirectory(tenantSlug, { enabled: office });
  const careManagers = (directory.data?.people ?? []).filter((p) => p.does_care_management);
  const queryClient = useQueryClient();
  const contact = useSupportContact();
  const [gaps, setGaps] = useState<{ date?: string; rosterId?: string }>({});

  const save = useMutation({
    mutationFn: (input: CareVisitInput) => careVisitsApi.create(tenantSlug, clientId, input),
    onSuccess: async () => {
      await refreshAfterCareVisitChange(queryClient, tenantSlug, clientId);
      onClose();
    },
  });

  const set = (change: Partial<VisitDraft>): void => onDraft({ ...draft, ...change });
  const press = (): void => {
    const found: { date?: string; rosterId?: string } = {};
    if (office && draft.rosterId === '') found.rosterId = 'Choose the care manager.';
    if (draft.date === '') found.date = 'Choose the date of the visit.';
    setGaps(found);
    if (Object.keys(found).length > 0) return;
    const note = draft.note.trim();
    save.mutate({
      ...(office ? { staff_roster_id: draft.rosterId } : {}),
      scheduled_for: draft.date,
      scheduled_time: draft.time === '' ? null : draft.time,
      note: note === '' ? null : note,
    });
  };

  return (
    <Dialog
      title={title}
      size="xl"
      testId="visit-dialog"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" data-testid="visit-save" loading={save.isPending} disabled={save.isPending} onClick={press}>
            Add visit
          </Button>
        </>
      }
    >
      {header}
      {/* The same grid as the Appointment face: one left edge, one rhythm.
          Date and time sit side by side at every width. */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-3 py-2">
        {office && (
          <div className="col-span-2">
            <Field label="Care manager" htmlFor="visit-care-manager" required error={gaps.rosterId}>
              <SelectField
                size="field"
                id="visit-care-manager"
                data-testid="visit-care-manager"
                className="w-full"
                value={draft.rosterId}
                onChange={(e) => set({ rosterId: e.target.value })}
              >
                <option value="">Choose a care manager</option>
                {careManagers.map((p) => (
                  <option key={p.roster_id} value={p.roster_id}>
                    {p.full_name}
                  </option>
                ))}
              </SelectField>
            </Field>
          </div>
        )}
        <Field label="Date" htmlFor="visit-date" required error={gaps.date}>
          <DateInput
            id="visit-date"
            size="field"
            data-testid="visit-date"
            className="w-full"
            value={draft.date}
            onChange={(e) => set({ date: e.target.value })}
          />
        </Field>
        <Field label="Time" htmlFor="visit-time">
          <input
            id="visit-time"
            type="time"
            data-testid="visit-time"
            className={FLD}
            value={draft.time}
            onChange={(e) => set({ time: e.target.value })}
          />
        </Field>
        <div className="col-span-2">
          <Field label="Note" htmlFor="visit-note">
            <TextInput
              id="visit-note"
              data-testid="visit-note"
              className={FLD}
              maxLength={VISIT_NOTE_MAX}
              value={draft.note}
              onChange={(e) => set({ note: e.target.value })}
            />
          </Field>
        </div>
      </div>
      {save.isError && (
        <p role="alert" data-testid="visit-error" className="text-xs text-pepper-deep">
          {/* A seat that reads the record without keeping it reads the
              server's own sentence (care_record_read_only). */}
          {serverSentence(save.error, 'record_edits') ?? pageWriteText(save.error, contact)}
        </p>
      )}
    </Dialog>
  );
}
